import z from 'zod'

import {
  HOMEPAGE_VIEW_ANALYTICS,
  HOMEPAGE_VIEW_SCORECARD,
  HomepageDefaultsSchema,
} from './homepage'

export const TEMPLATE_ORG_ID = '46bc2886-3b3e-4c9d-ba4f-c4ff12a7b98c'

export const CallProfile = z.enum([
  'customer_service',
  'technical_support',
  'sales_order_taking',
  'retention',
  'healthcare_patient_services',
  'account_management',
])
export type CallProfile = z.infer<typeof CallProfile>

export const CALL_PROFILE_LABELS: Record<CallProfile, string> = {
  customer_service: 'Customer Service',
  technical_support: 'Technical Support',
  sales_order_taking: 'Sales / Order Taking',
  retention: 'Retention',
  healthcare_patient_services: 'Healthcare / Patient Services',
  account_management: 'Account Management',
}

export const OrganizationSetup = z.object({
  welcome: z.boolean().default(false),
  callProfiles: z.array(CallProfile).optional(),
})
export type OrganizationSetup = z.infer<typeof OrganizationSetup>

export const ScorecardSettings = z.object({
  recordingDuration: z
    .object({
      min: z.number().int().min(0).default(0), // seconds
      max: z.number().int().min(0).nullable().default(null), // seconds, null = no limit
    })
    .default({ min: 0, max: null }),
  messageCountMin: z.number().int().min(0).nullable().default(null), // minimum messages, null = disabled
  primaryAgentInvolvement: z.number().int().min(1).max(4).default(2), // 1=80%, 2=60%, 3=40%, 4=any
  speakerRatioLimit: z.number().int().min(50).max(100).nullable().default(null), // percentage, null = disabled
  excludeVoicemails: z.boolean().default(false), // exclude voicemails from auto-evaluation
})
export type ScorecardSettings = z.infer<typeof ScorecardSettings>

export const RecordingType = z.enum(['auto-detect', 'stereo', 'mono'])
export type RecordingType = z.infer<typeof RecordingType>

export const TranscriptionSettings = z.object({
  recordingType: RecordingType.default('auto-detect'),
  channelIdentification: z
    .array(z.enum(['Agent', 'Customer', 'System']))
    .default(['Customer', 'Agent', 'System']),
})
export type TranscriptionSettings = z.infer<typeof TranscriptionSettings>

export const PIIRedactionSettings = z.object({
  enabled: z.boolean().default(true),
  redactSSN: z.boolean().default(true),
  redactBirthday: z.boolean().default(true),
  redactCreditCard: z.boolean().default(true),
})
export type PIIRedactionSettings = z.infer<typeof PIIRedactionSettings>

export const OrganizationSettings = z.object({
  timezone: z.string().nullable().default(null),
  scorecard: ScorecardSettings.default({
    recordingDuration: { min: 0, max: null },
    messageCountMin: null,
    primaryAgentInvolvement: 2,
    speakerRatioLimit: null,
    excludeVoicemails: false,
  }),
  transcription: TranscriptionSettings.default({
    recordingType: 'auto-detect',
    channelIdentification: ['Customer', 'Agent', 'System'],
  }),
  homepageDefaults: HomepageDefaultsSchema.default({
    agent: HOMEPAGE_VIEW_SCORECARD,
    admin: HOMEPAGE_VIEW_ANALYTICS,
  }),
  piiRedaction: PIIRedactionSettings.default({
    enabled: true,
    redactSSN: true,
    redactBirthday: true,
    redactCreditCard: true,
  }),
})
export type OrganizationSettings = z.infer<typeof OrganizationSettings>

export const MAX_FLAGS_PER_ORG = 20

export const OrganizationMutationSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters long')
    .max(60, 'Name must be less than 60 characters'),
  alias: z
    .string()
    .min(2, 'URL must be at least 2 characters long')
    .max(60, 'URL must be less than 60 characters')
    .regex(/^[a-z0-9-]+$/, 'URL can only contain lowercase letters, numbers, and dashes'),
  setup: z
    .object({
      welcome: z.boolean(),
      callProfiles: z.array(CallProfile).optional(),
    })
    .partial()
    .optional(),
  iconFileId: z.string().nullish(),
  settings: OrganizationSettings.partial().optional(),
})
